import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Package, ArrowLeft, LogOut, Eye, Send, Truck, 
  CheckCircle, XCircle, Clock, RefreshCw, Search,
  FileText, Mail, Trash2, AlertTriangle
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  { value: 'processing', label: 'Processing', color: 'bg-indigo-100 text-indigo-700', icon: RefreshCw },
  { value: 'shipped', label: 'Shipped', color: 'bg-purple-100 text-purple-700', icon: Truck },
  { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-orange-100 text-orange-700', icon: Truck },
  { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  { value: 'returned', label: 'Returned', color: 'bg-gray-100 text-gray-700', icon: RefreshCw },
  { value: 'refunded', label: 'Refunded', color: 'bg-pink-100 text-pink-700', icon: RefreshCw },
  { value: 'expired', label: 'Expired', color: 'bg-gray-200 text-gray-600', icon: AlertTriangle }
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { authenticated, loading: authLoading, logout, getAuthHeader, currentUser } = useAdminAuth();
  const navigate = useNavigate();
  
  // Check if current user is super admin
  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.roles?.includes('super_admin');

  const [updateForm, setUpdateForm] = useState({
    status: '',
    payment_status: '',
    tracking_number: '',
    shipping_provider: '',
    notes: ''
  });

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchOrders();
    }
  }, [authenticated, filterStatus]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`, {
        headers: getAuthHeader()
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const selectOrder = (order) => {
    setSelectedOrder(order);
    setUpdateForm({
      status: order.status || 'pending',
      payment_status: order.payment_status || 'pending',
      tracking_number: order.tracking_number || '',
      shipping_provider: order.shipping_provider || '',
      notes: order.notes || ''
    });
  };

  const updateOrder = async () => {
    if (!selectedOrder) return;
    
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/orders/${selectedOrder._id}`, updateForm, {
        headers: getAuthHeader()
      });
      toast.success('Order updated successfully');
      fetchOrders();
      
      // Update selected order locally
      setSelectedOrder(prev => ({ ...prev, ...updateForm }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setUpdating(false);
    }
  };

  const sendInvoice = async () => {
    if (!selectedOrder) return;
    
    const email = selectedOrder.customer?.email;
    if (!email) {
      toast.error('Customer email not found');
      return;
    }
    
    setSendingInvoice(true);
    try {
      await axios.post(`${API}/admin/orders/${selectedOrder._id}/send-invoice`, {
        email,
        notes: updateForm.notes
      }, {
        headers: getAuthHeader()
      });
      toast.success(`Invoice sent to ${email}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    } finally {
      setSendingInvoice(false);
    }
  };

  const deleteOrder = async () => {
    if (!selectedOrder) return;
    
    const orderNumber = selectedOrder.order_number || selectedOrder._id?.substring(0, 8);
    
    if (!window.confirm(
      `Are you sure you want to delete Order #${orderNumber}?\n\n` +
      `${selectedOrder.payment_status === 'pending' ? '⚠️ Stock will be restored for this unpaid order.\n\n' : ''}` +
      `This action cannot be undone.`
    )) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/orders/${selectedOrder._id}`, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message || 'Order deleted successfully');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    return ORDER_STATUSES.find(s => s.value === status) || ORDER_STATUSES[0];
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.customer?.name?.toLowerCase().includes(search) ||
      order.customer?.email?.toLowerCase().includes(search)
    );
  });

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Order Management</h1>
            <p className="text-sm text-gray-500">View and manage customer orders</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search orders..."
              className="pl-10 w-64"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Statuses</option>
            {ORDER_STATUSES.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <Button variant="outline" onClick={fetchOrders}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Orders List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Orders ({filteredOrders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No orders found
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {filteredOrders.map((order) => {
                      const statusInfo = getStatusInfo(order.status);
                      const StatusIcon = statusInfo.icon;
                      const isSelected = selectedOrder?._id === order._id;
                      
                      return (
                        <div
                          key={order._id}
                          onClick={() => selectOrder(order)}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
                              : 'hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">#{order.order_number || order._id.slice(-8)}</span>
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${statusInfo.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>{order.customer?.name || 'Customer'}</p>
                            <p className="text-xs">{formatDate(order.created_at)}</p>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm">{order.items?.length || 0} items</span>
                            <span className="font-semibold text-green-600">{formatPrice(order.total_amount)}</span>
                          </div>
                          {order.invoice_sent && (
                            <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                              <Mail className="h-3 w-3" /> Invoice sent
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Order Details */}
          <div>
            {selectedOrder ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Order Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Customer Info */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Customer</h4>
                    <p className="text-sm">{selectedOrder.customer?.name}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.customer?.email}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.customer?.phone}</p>
                    <p className="text-sm text-gray-600">{selectedOrder.customer?.address}</p>
                  </div>

                  {/* Items */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Items</h4>
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span>{item.name} × {item.quantity}</span>
                        <span>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-semibold mt-2 pt-2 border-t">
                      <span>Total</span>
                      <span className="text-green-600">{formatPrice(selectedOrder.total_amount)}</span>
                    </div>
                  </div>

                  {/* Update Form */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <select
                        value={updateForm.status}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        {ORDER_STATUSES.map(status => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Payment Status</label>
                      <select
                        value={updateForm.payment_status}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, payment_status: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Tracking Number</label>
                      <Input
                        value={updateForm.tracking_number}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, tracking_number: e.target.value }))}
                        placeholder="Enter tracking number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Shipping Provider</label>
                      <Input
                        value={updateForm.shipping_provider}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, shipping_provider: e.target.value }))}
                        placeholder="e.g., DHL, FedEx"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Notes</label>
                      <textarea
                        value={updateForm.notes}
                        onChange={(e) => setUpdateForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border rounded-md"
                        placeholder="Internal notes..."
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <Button onClick={updateOrder} disabled={updating} className="w-full">
                      {updating ? 'Updating...' : 'Update Order'}
                    </Button>
                    <Button 
                      onClick={sendInvoice} 
                      disabled={sendingInvoice} 
                      variant="outline"
                      className="w-full"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {sendingInvoice ? 'Sending...' : 'Send Invoice Email'}
                    </Button>
                    
                    {/* Delete Button - Super Admin Only */}
                    {isSuperAdmin && (
                      <Button 
                        onClick={deleteOrder} 
                        disabled={deleting}
                        variant="destructive"
                        className="w-full"
                        data-testid="delete-order-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {deleting ? 'Deleting...' : 'Delete Order'}
                      </Button>
                    )}
                  </div>
                  
                  {/* Expiration Warning */}
                  {selectedOrder.status === 'pending' && selectedOrder.payment_status === 'pending' && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Payment Pending</p>
                          <p className="text-amber-700">
                            This order will auto-expire if not paid within 48 hours of creation.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Expired Order Notice */}
                  {selectedOrder.status === 'expired' && (
                    <div className="mt-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-gray-800">Order Expired</p>
                          <p className="text-gray-600">
                            {selectedOrder.expiration_reason || 'This order was auto-expired due to non-payment within 48 hours. Stock has been restored.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select an order to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;
